import { supabase } from "@/integrations/supabase/client";
import { ConversationAnalysisOptions, ConversationDetails } from '@/types/conversation/conversation';
import { UserService } from "@/services/users/UserService";
import { ConversationExcelExport } from "@/services/export/ConversationExcelExport";

export class ConversationAnalysisService {
  private static applyAgentFilter(query: any, agentIds: string[], selectedAgent?: string | null) {
    if (selectedAgent) {
      return query.eq("agent_id", selectedAgent);
    } else {
      return query.in("agent_id", agentIds);
    }
  }

  private static mapTranscript(transcript: any[]): any[] {
    return transcript.map((item: any) => ({
      speaker: item.role || (item.agent ? "agent" : "user"),
      text: item.message || item.text || "",
      timestamp: item.time_in_call_secs || 0,
    }));
  }

  static calculateHasAnalysis(detail: ConversationDetails): boolean {
    return !!(detail.summary || detail.goals?.length || detail.topics?.length || Object.keys(detail.lead_info || {}).length);
  }

  static async getConversationAnalysisForUser(options: ConversationAnalysisOptions = {}): Promise<{
    data: ConversationDetails[];
    totalCount: number;
    page: number;
    pageSize: number;
  }> {
    try {
      const {
        data: { user },
      } = await UserService.getUser();
      if (!user) throw new Error("You must be logged in.");

      const { page = 1, pageSize = 20, agentId, analysisFilter = "all", dateRange, agents } = options;

      if (!agents || agents.length === 0) {
        console.log("No agents provided to ConversationAnalysisService");
        return { data: [], totalCount: 0, page, pageSize };
      }

      const offset = (page - 1) * pageSize;

      let query = supabase
        .from("conversation_details")
        .select("*, conversation:conversations!inner(type)", { count: "exact" })
        .or("type.neq.mailbox,type.is.null", { referencedTable: "conversations" });

      if (agentId) {
        console.log(`Filtering by specific agent_id: ${agentId}`);
        query = query.eq("agent_id", agentId);
      } else {
        const agentIds = agents.map((a) => a.id);
        console.log(`Filtering by all user agent_ids (no specific agent selected):`, agentIds);
        query = query.in("agent_id", agentIds);
      }

      if (analysisFilter === "analyzed") {
        query = query.or("summary.not.is.null,goals.not.is.null,topics.not.is.null,lead_info.not.is.null");
      } else if (analysisFilter === "unanalyzed") {
        query = query.is("summary", null).is("goals", null).is("topics", null).is("lead_info", null);
      }

      if (dateRange && dateRange !== "all") {
        const daysBack = parseInt(dateRange.replace("d", ""));
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysBack);
        query = query.gte("conversation_date", cutoffDate.toISOString());
      }

      query = query.order("conversation_date", { ascending: false }).range(offset, offset + pageSize - 1);

      const { data: conversationDetails, error: detailsError, count } = await query;

      if (detailsError) {
        console.error("Error fetching conversation details:", detailsError);
        return { data: [], totalCount: 0, page, pageSize };
      }

      const conversations: ConversationDetails[] = [];

      for (const detail of conversationDetails || []) {
        const agent = agents.find((a) => a.id === detail.agent_id);
        if (!agent) continue;

        const conversationWithAnalysis: ConversationDetails = {
          ...detail,
          has_analysis: this.calculateHasAnalysis(detail),
          genie_name: agent.name,
          genie_branding: (agent as any).branding,
        };

        if (conversations.length === 0) {
          console.log("First conversation entry being added:", conversationWithAnalysis);
        }

        conversations.push(conversationWithAnalysis);
      }

      return {
        data: conversations,
        totalCount: count || 0,
        page,
        pageSize,
      };
    } catch (error) {
      console.error("Error fetching conversation analysis:", error);
      throw error;
    }
  }

  static async exportAnalysisToExcel(
    selectedAgentId: string,
    options: { dateRange?: string; analysisFilter?: "all" | "analyzed" | "unanalyzed" } = {}
  ): Promise<{ genieName: string; count: number }> {
    try {
      const {
        data: { user },
      } = await UserService.getUser();
      if (!user) throw new Error("You must be logged in.");

      const { dateRange, analysisFilter } = options;

      let query = supabase
        .from("conversation_details")
        .select(
          `
          *,
          agents!inner(name)
        `
        )
        .eq("agent_id", selectedAgentId);

      // Apply date range filter
      if (dateRange && dateRange !== "all") {
        const daysBack = parseInt(dateRange.replace("d", ""));
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysBack);
        query = query.gte("conversation_date", cutoffDate.toISOString());
      }

      // Apply analysis filter
      if (analysisFilter === "analyzed") {
        query = query.or("summary.not.is.null,goals.not.is.null,topics.not.is.null,lead_info.not.is.null");
      } else if (analysisFilter === "unanalyzed") {
        query = query.is("summary", null).is("goals", null).is("topics", null).is("lead_info", null);
      }

      const { data: conversationDetails, error } = await query.order("conversation_date", { ascending: false });

      if (error) {
        console.error("Error fetching conversation details for export:", error);
        throw new Error("Failed to fetch conversation details");
      }

      if (!conversationDetails || conversationDetails.length === 0) {
        throw new Error("No conversations found for the selected agent");
      }

      const genieName = conversationDetails[0]?.agents?.name || "Unknown";

      const conversations = conversationDetails.map((detail) => ({
        ...detail,
        has_analysis: this.calculateHasAnalysis(detail),
        genie_name: detail.agents?.name || "Unknown",
      }));

      const workbook = ConversationExcelExport.generateExcelWorkbook(conversations);
      ConversationExcelExport.downloadWorkbook(workbook, genieName);

      return { genieName, count: conversations.length };
    } catch (error) {
      console.error("Error exporting analysis to Excel:", error);
      throw error;
    }
  }

  static exportConversationsToExcel(
    conversations: ConversationDetails[],
    fileLabel: string,
  ): { count: number } {
    if (!conversations.length) {
      throw new Error("No conversations to export");
    }
    const normalised = conversations.map((c) => ({
      ...c,
      has_analysis: c.has_analysis ?? this.calculateHasAnalysis(c),
      genie_name: c.genie_name || "Unknown",
    }));
    const workbook = ConversationExcelExport.generateExcelWorkbook(normalised as any);
    ConversationExcelExport.downloadWorkbook(workbook, fileLabel);
    return { count: normalised.length };
  }

  static getAnalysisStats(conversations: ConversationDetails[]) {
    const total = conversations.length;
    const withAnalysis = conversations.filter((c) => c.has_analysis).length;
    const withLeadInfo = conversations.filter((c) => c.lead_info && Object.keys(c.lead_info).length > 0).length;
    const thisWeek = conversations.filter((c) => {
      if (!c.conversation_date) return false;
      const convDate = new Date(c.conversation_date);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return convDate >= weekAgo;
    }).length;

    return { total, withAnalysis, withLeadInfo, thisWeek };
  }

  static async getConversationStatsForUser(options: {
    selectedAgent?: string | null;
    agents: Array<{ id: string; name: string; elevenlabs_id?: string; branding?: any }>;
  }) {
    try {
      const {
        data: { user },
      } = await UserService.getUser();
      if (!user) throw new Error("You must be logged in.");

      const { selectedAgent, agents } = options;

      if (!agents || agents.length === 0) {
        return { total: 0, withAnalysis: 0, withLeadInfo: 0, thisWeek: 0 };
      }

      // Build query for ALL conversations (no pagination, no filters except agent)
      let query = supabase.from("conversation_details").select(
        `
          id,
          agent_id,
          conversation_date,
          summary,
          goals,
          topics,
          lead_info
        `
      );

      const agentIds = agents.map((a) => a.id);
      query = this.applyAgentFilter(query, agentIds, selectedAgent);

      const { data: conversationDetails, error: detailsError } = await query;

      if (detailsError) {
        console.error("Error fetching conversation stats:", detailsError);
        return { total: 0, withAnalysis: 0, withLeadInfo: 0, thisWeek: 0 };
      }

      const conversations = (conversationDetails || []).map((detail) => ({
        ...detail,
        has_analysis: this.calculateHasAnalysis(detail),
      }));

      return this.getAnalysisStats(conversations);
    } catch (error) {
      console.error("Error fetching conversation stats:", error);
      return { total: 0, withAnalysis: 0, withLeadInfo: 0, thisWeek: 0 };
    }
  }
}
